import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/**
 * Emailing an invoice to a customer, or chasing one that is overdue.
 *
 * The address is shown and editable rather than assumed: plenty of businesses
 * have a separate accounts inbox, and silently mailing the contact on file is
 * how an invoice goes missing.
 */
export default function SendDocumentDialog({ mode, doc, onClose, onSent }) {
  const open = !!mode;
  const remind = mode === "REMIND";
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    to_email: "", message: "", attach_pdf: true, include_link: true,
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      to_email: doc?.party_email || "",
      message: "", attach_pdf: true, include_link: true,
    });
  }, [open, doc]);

  const send = async () => {
    if (!form.to_email.trim()) { toast.error("Enter an email address"); return; }
    setBusy(true);
    try {
      const path = remind ? "remind" : "send";
      const { data } = await api.post(`/documents/${doc.id}/${path}`, form);
      toast.success(remind ? `Reminder sent to ${data.to_email}`
                           : `Invoice sent to ${data.to_email}`);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="send-document-dialog">
        <DialogHeader>
          <DialogTitle>{remind ? "Send a payment reminder" : "Email this invoice"}</DialogTitle>
          <DialogDescription>
            {remind
              ? `${doc?.document_number} has ${doc?.balance_due > 0 ? "an outstanding balance" : "no balance"}. The reminder goes out under your business name.`
              : "The PDF is attached and the amount is restated in the message, so it reads on a phone."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">To</Label>
            <Input value={form.to_email} data-testid="send-email-input"
              placeholder="accounts@customer.com"
              onChange={(e) => setForm((f) => ({ ...f, to_email: e.target.value }))} />
            {!doc?.party_email && (
              <p className="text-[11px] text-amber-700">
                This customer has no email on file — what you type here is used once.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Message (optional)</Label>
            <Textarea rows={3} value={form.message} data-testid="send-message-input"
              placeholder={remind ? "A short note before the reminder text" : "Anything you want to say alongside it"}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={form.attach_pdf} data-testid="send-attach-toggle"
              onCheckedChange={(v) => setForm((f) => ({ ...f, attach_pdf: v }))} />
            Attach the PDF
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={form.include_link} data-testid="send-link-toggle"
              onCheckedChange={(v) => setForm((f) => ({ ...f, include_link: v }))} />
            Include a link to view it online
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy} data-testid="send-confirm"
            className="gap-1.5 bg-blue-600 hover:bg-blue-700">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
            {remind ? "Send reminder" : "Send invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
